import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { SideSheet } from '@app/app/_components/SideSheet/SideSheet';
import { Heading } from '@app/app/_components/Heading/Heading';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { Button } from '@app/app/_components/Button/Button';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { CheckThinIcon } from '@app/app/_icons/CheckThinIcon';
import { InsuredPersonsStep } from '@app/app/esales/travel/_sections/steps/InsuredPersonsStep/InsuredPersonsStep';
import { InsuredTraveler } from '@app/domain/model/eshop/travel/InsuredTraveler';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTravelInsuranceResult } from '@app/service/eshop/travel/actions/purchaseTravelInsurance';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { validateInsuredPersons } from '@app/app/esales/travel/helpers/validateInsuredPersons';
import { useTranslations } from 'next-intl';
import React, { useCallback, useState, useMemo } from 'react';

interface Props {
    insuredPersons: InsuredTraveler[];
    policyholderIsTraveler: boolean;
    formState: FormState<PurchaseTravelInsuranceResult>;
    onSaveTravelers: (travelers: InsuredTraveler[]) => void;
    onChangeNumberOfPersons: () => void;
    editPolicyholderInfo: () => void;
    changeAgeOfTraveler: () => void;
    editTravelers: () => void;
    isCompany: boolean;
}

export function TravelersSummaryCard({
    insuredPersons,
    policyholderIsTraveler,
    formState,
    onSaveTravelers,
    onChangeNumberOfPersons,
    editPolicyholderInfo,
    changeAgeOfTraveler,
    editTravelers,
    isCompany,
}: Props) {
    const t = useTranslations();
    const [sideSheetOpen, setSideSheetOpen] = useState(false);

    const [editingTravelers, setEditingTravelers] = useState<InsuredTraveler[]>([]);
    const [localViolations, setLocalViolations] = useState<ViolationMap | null>(null);

    const hasAgeMismatch = useMemo(() => {
        return editingTravelers.some(person => {
            if (!person.personalCode) return false;
            const parsedCode = parseLithuanianPersonalCode(person.personalCode);
            return parsedCode.isValid && parsedCode.age && parsedCode.age !== person.age;
        });
    }, [editingTravelers]);

    const handleLocalNameChange = useCallback(
        (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const name = e.target.value;
            setEditingTravelers(prev => prev.map(person => (person.id === id ? { ...person, name } : person)));
            setLocalViolations(null);
        },
        [],
    );

    const handleLocalSurnameChange = useCallback(
        (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const surname = e.target.value;
            setEditingTravelers(prev => prev.map(person => (person.id === id ? { ...person, surname } : person)));
            setLocalViolations(null);
        },
        [],
    );

    const handleLocalPersonalCodeChange = useCallback(
        (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const personalCode = e.target.value;
            setEditingTravelers(prev => prev.map(person => (person.id === id ? { ...person, personalCode } : person)));
            setLocalViolations(null);
        },
        [],
    );

    const handleEditTravelersClick = useCallback(() => {
        if (policyholderIsTraveler) {
            editTravelers();
        } else {
            setEditingTravelers(insuredPersons.map(person => ({ ...person })));
            setLocalViolations(null);
            setSideSheetOpen(true);
        }
    }, [policyholderIsTraveler, editTravelers, insuredPersons]);

    const handleCloseSideSheet = useCallback(() => {
        setSideSheetOpen(false);
        setEditingTravelers([]);
        setLocalViolations(null);
    }, []);

    const handleSideSheetChangeNumberOfPersons = useCallback(() => {
        setSideSheetOpen(false);
        setEditingTravelers([]);
        setLocalViolations(null);
        onChangeNumberOfPersons();
    }, [onChangeNumberOfPersons]);

    const handleSideSheetEditPolicyholderInfo = useCallback(() => {
        setSideSheetOpen(false);
        setEditingTravelers([]);
        setLocalViolations(null);
        editPolicyholderInfo();
    }, [editPolicyholderInfo]);

    const handleSideSheetChangeAgeOfTraveler = useCallback(() => {
        setSideSheetOpen(false);
        setEditingTravelers([]);
        setLocalViolations(null);
        changeAgeOfTraveler();
    }, [changeAgeOfTraveler]);

    const handleSave = useCallback(() => {
        const violations = validateInsuredPersons(editingTravelers);

        if (violations) {
            setLocalViolations(violations);
            return;
        }

        onSaveTravelers(editingTravelers);
        setSideSheetOpen(false);
        setEditingTravelers([]);
        setLocalViolations(null);
    }, [editingTravelers, onSaveTravelers]);

    const sideSheetHeader = (
        <div className="flex items-center justify-between rounded-t-xl border-b border-neutral-300 px-6 py-4">
            <div className="flex items-center gap-3">
                <button type="button" className="cursor-pointer" onClick={handleCloseSideSheet}>
                    <ArrowLeftIcon className="h-5 w-5 text-neutral-500" />
                </button>
                <Heading level="h3">{t('eshop.travel.summaryStep.editTravelers')}</Heading>
            </div>
        </div>
    );

    const sideSheetFooter = (
        <div className="flex h-min w-full items-center justify-between gap-6 bg-white px-6 py-4 shadow-2xl">
            <Button
                variant="outlined"
                className="w-full"
                rightAddon={<CloseIcon className="size-4" />}
                onClick={handleCloseSideSheet}>
                {t('general.cancel')}
            </Button>
            <Button
                className="w-full"
                rightAddon={<CheckThinIcon className="size-5" />}
                onClick={handleSave}
                disabled={hasAgeMismatch}>
                {t('general.save')}
            </Button>
        </div>
    );

    return (
        <>
            <CollapsablePanel
                addon={<TextButton onClick={handleEditTravelersClick}>{t('general.edit')}</TextButton>}
                collapsable={false}
                label={t('eshop.travel.summaryStep.allInsuredPeople')}>
                <div className="grid w-full grid-cols-2 gap-4 px-6 pt-6">
                    {insuredPersons.map(person => (
                        <React.Fragment key={person.id}>
                            <ReadOnlyField
                                label={t(
                                    'eshop.travel.summaryStep.nameSurname',
                                )}>{`${person.name} ${person.surname}`}</ReadOnlyField>
                            <ReadOnlyField label={t('eshop.travel.summaryStep.personalCode')}>
                                {person.personalCode}
                            </ReadOnlyField>
                        </React.Fragment>
                    ))}
                </div>
            </CollapsablePanel>

            <SideSheet open={sideSheetOpen} header={sideSheetHeader} footer={sideSheetFooter} contentClassName="p-6">
                {sideSheetOpen && (
                    <InsuredPersonsStep
                        insuredPersons={editingTravelers}
                        handleNameChange={handleLocalNameChange}
                        handleSurnameChange={handleLocalSurnameChange}
                        handlePersonalCodeChange={handleLocalPersonalCodeChange}
                        violations={localViolations ?? undefined}
                        policyholderIsTraveler={policyholderIsTraveler}
                        onChangeNumberOfPersons={handleSideSheetChangeNumberOfPersons}
                        formState={formState}
                        editPolicyholderInfo={handleSideSheetEditPolicyholderInfo}
                        changeAgeOfTraveler={handleSideSheetChangeAgeOfTraveler}
                        isCompany={isCompany}
                    />
                )}
            </SideSheet>
        </>
    );
}
